/**
 * The catalogue asks the server for one page at a time.
 *
 * It used to download every product and filter in the browser, which stopped
 * working at scale — a 1000-product cap silently hid the rest of a 5000-SKU
 * vendor. Everything the page filters, searches and sorts by is now a query
 * parameter; this file is the one place that spells them.
 */

export type DraftFilter = 'complete' | 'draft' | 'all';

export interface CatalogFilters {
    familyId?: string;
    familySlug?: string;
    /** Submitted search text (sent on Enter, not as the vendor types). */
    search?: string;
    visibility?: 'all' | 'visible' | 'hidden';
    draft?: DraftFilter;
    productTypes?: string[];
    surfaces?: string[];
    sizes?: string[];
    collections?: string[];
    colorFamily?: string[];
    materialFamily?: string[];
    style?: string[];
    finish?: string[];
    /** Restrict to these products — an AI search result. */
    uuids?: string[];
}

export interface CatalogPageRequest extends CatalogFilters {
    /** 1-indexed. */
    page: number;
    limit: number;
    /** The vendor's saved order preference: NAME_ASC, DATE_NEW_OLD, MANUAL, … */
    order?: string;
}

export interface CatalogFacets {
    /** Default products in the catalogue (current status filter), before any other filter. */
    total: number;
    /** Every live SKU, variants included — what the plan limit is measured against. */
    skuCount: number;
    familyCounts: Record<string, number>;
    productTypes: string[];
    surfaces: string[];
    sizes: string[];
    collections: string[];
    appearance: {
        analysed: number;
        colorFamily: Array<[string, number]>;
        materialFamily: Array<[string, number]>;
        style: Array<[string, number]>;
    };
}

export const EMPTY_CATALOG_FACETS: CatalogFacets = {
    total: 0,
    skuCount: 0,
    familyCounts: {},
    productTypes: [],
    surfaces: [],
    sizes: [],
    collections: [],
    appearance: { analysed: 0, colorFamily: [], materialFamily: [], style: [] },
};

/** How a saved order preference is asked of the server. DISCOVERY is a visualizer order; the list shows newest first. */
export function sortParams(order?: string): { sortBy: string; sortOrder: 'asc' | 'desc' } {
    switch (order) {
        case 'NAME_ASC': return { sortBy: 'name', sortOrder: 'asc' };
        case 'NAME_DESC': return { sortBy: 'name', sortOrder: 'desc' };
        case 'DATE_OLD_NEW': return { sortBy: 'createdAt', sortOrder: 'asc' };
        case 'MANUAL': return { sortBy: 'order', sortOrder: 'asc' };
        default: return { sortBy: 'createdAt', sortOrder: 'desc' };
    }
}

const DRAFT_PARAM: Record<DraftFilter, string> = { complete: 'exclude', draft: 'only', all: 'all' };

/**
 * Query string for the product list / facets endpoints. Always default
 * products — a row stands for a product and its variants.
 */
export function catalogQueryString(request: Partial<CatalogPageRequest>): string {
    const params = new URLSearchParams();
    params.set('isDefault', 'true');
    params.set('draft', DRAFT_PARAM[request.draft || 'all']);

    if (request.page) params.set('page', String(request.page));
    if (request.limit) params.set('limit', String(request.limit));
    if (request.order !== undefined) {
        const { sortBy, sortOrder } = sortParams(request.order);
        params.set('sortBy', sortBy);
        params.set('sortOrder', sortOrder);
    }
    if (request.familyId) params.set('familyId', request.familyId);
    if (request.familySlug) params.set('familySlug', request.familySlug);
    if (request.search?.trim()) params.set('search', request.search.trim());
    if (request.visibility === 'visible') params.set('isVisible', 'true');
    if (request.visibility === 'hidden') params.set('isVisible', 'false');

    const lists: Array<keyof CatalogFilters> = [
        'productTypes', 'surfaces', 'sizes', 'collections',
        'colorFamily', 'materialFamily', 'style', 'finish', 'uuids',
    ];
    for (const key of lists) {
        const values = request[key] as string[] | undefined;
        // Repeated params, not a joined string: a collection or size label may contain a comma.
        values?.forEach((value) => params.append(key, value));
    }
    // An AI search that found nothing must show nothing, not everything.
    if (request.uuids && request.uuids.length === 0) params.append('uuids', '__none__');

    return `?${params.toString()}`;
}
